import { createContext, useContext, type MutableRefObject } from 'react'
import type { TaskPickItem } from '../api/taskPickApi'

/** 侧边栏里现在显示什么：会话列表，或一个会话（像 mobile 一样的栈：会话页带返回） */
export type ChatPanelView = { kind: 'list' } | { kind: 'thread'; id: string }

/**
 * 侧边栏的摆法（与 AI 助手停靠同一套尺寸：windowGeometry 的 DOCK_WIDTH / DOCK_MIN_VIEWPORT / TOP_OFFSET）：
 * - dock：视口够宽，贴右、上沿贴顶栏、到底，页面主体让出同样宽度（挤压，不覆盖）；
 * - overlay：视口不够宽，同样贴右但盖在页面上（页面不让位）；
 * - fullscreen：手机宽度，整屏。
 */
export type ChatPanelLayout = 'dock' | 'overlay' | 'fullscreen'

/**
 * 从任务里给人发消息时带来的任务（对齐 mobile TaskChatModal 的 initialTask）：只交给这个会话，
 * 会话页把它放进输入框上方的「About: <任务>」条（同插入任务的任务条，发送时带 refs），放完就调 consumeAboutTask 清掉。
 * seq 每次从任务进来都变：用户点 × 去掉后，再从同一个任务进来照样能重新带上。
 */
export interface ChatPanelAboutTask {
  conversationId: string
  task: TaskPickItem
  seq: number
}

export interface MessageUserOptions {
  /** 从任务进来：会话输入框上方预置「About: <任务>」 */
  aboutTask?: TaskPickItem
}

export interface ChatPanelApi {
  open: boolean
  view: ChatPanelView
  layout: ChatPanelLayout
  /** 页面主体 / 页面 flyout 要让出的右侧宽度（px）；不停靠 = 0 */
  dockInset: number
  /** 有 Messages 的读写权限（system:chat:query + read + send）：能从别处（任务详情头像）直接发起单聊 */
  canStartChat: boolean
  /** 当前用户 id（任务详情里「对象是自己不显示」用） */
  me: string | null
  /** 打开侧边栏到会话列表 */
  openList: () => void
  /** 打开侧边栏里的某个会话 */
  openConversation: (conversationId: string) => void
  close: () => void
  /** 顶栏按钮：开着 = 关；关着 = 打开到会话列表 */
  toggle: () => void
  /**
   * 和某人单聊：手上的会话列表里已有就直接打开，否则 POST /conversations（direct 已存在后端返回原会话）。
   * 403 CHAT_CONTACT_NOT_ALLOWED / 其他失败按码提示。返回是否打开了。
   * 带 aboutTask（从任务详情进来）：打开的会话输入框上方预置这个任务。
   */
  messageUser: (userId: string, options?: MessageUserOptions) => Promise<boolean>
  /** 等会话页接走的任务（见 ChatPanelAboutTask）；打开别的会话 / 回列表 / 关闭时清掉 */
  aboutTask: ChatPanelAboutTask | null
  /** 会话页放好了（或按权限不放）：清掉这一次的（seq 不是当前的就不动） */
  consumeAboutTask: (seq: number) => void
  /** 顶栏按钮登记自己：关闭后焦点回到它 */
  triggerRef: MutableRefObject<HTMLElement | null>
}

export const ChatPanelContext = createContext<ChatPanelApi | null>(null)

export function useChatPanel(): ChatPanelApi {
  const ctx = useContext(ChatPanelContext)
  // 没有 provider = 挂载位置错了（应在 Layout 里包住顶栏与页面），直接报错，不静默降级
  if (!ctx) throw new Error('useChatPanel must be used inside <ChatPanelProvider>')
  return ctx
}

/**
 * 不一定在 Layout 里的地方用（任务详情也会出现在 WebView 整页、单测里）：没有 provider 返回 null，调用方不显示入口。
 */
export function useChatPanelOptional(): ChatPanelApi | null {
  return useContext(ChatPanelContext)
}
